// Angular
import { Injectable } from '@angular/core';

// Third party
import { IGetEventsDetailsResponse } from '@amwager/util-ts';

/** Per-leg map of runnerId -> selection value (e.g. "1", "A"). */
export type EncodedRunnerLeg = Record<string, string>;

/** Inputs needed to build a shareable wager deep-link. */
export interface IWagerShareLink {
  eventId: string;
  race: string | number;
  poolId: string;
  baseAmount: string | number;
  modifier: string | number;
  selectionRunners: EncodedRunnerLeg[];
}

/**
 * Reusable helpers for the Help panel — rules/codes CSV export and
 * encoded-runner share links (ports of the legacy web Help tab,
 * decoupled from the DOM/globals it used to read).
 */
@Injectable({ providedIn: 'root' })
export class HelpService {
  private readonly csvHeader =
    'Track Name,Track Code,Pool Code,Wager Min,Wager Max,Total Min,Total Max,Wager Increment';

  /**
   * Build the rules & codes CSV (one row per unique pool per event)
   * @param {IGetEventsDetailsResponse[]} events Available events
   * @returns {string} CSV text (header + rows)
   */
  buildRulesAndCodesCsv(events: IGetEventsDetailsResponse[]): string {
    const rows = [this.csvHeader];

    const sorted = [...(events ?? [])].sort((a, b) =>
      (a?.ToteResponse?.EventDetail?.EventName ?? '')
        .localeCompare(b?.ToteResponse?.EventDetail?.EventName ?? '')
    );

    for (const event of sorted) {
      const detail = event?.ToteResponse?.EventDetail;
      if (!detail?.Races?.length) continue;

      const seenPools = new Set<string>();
      for (const race of detail.Races) {
        for (const pool of race?.Pools ?? []) {
          if (seenPools.has(pool.PoolId)) continue;
          seenPools.add(pool.PoolId);

          rows.push([
            detail.EventName,
            detail.EventId,
            pool.PoolId,
            pool.MultipleBetMinimum,
            pool.Maximum,
            pool.WagerMinimum,
            pool.WBMaximum,
            pool.MultipleBetMinimum
          ].join(','));
        }
      }
    }

    return rows.join('\n');
  }

  /**
   * Date-stamped CSV file name: stake_settings_YYYY-MM-DD.csv
   * @param {Date} date Date to stamp (defaults to now)
   * @returns {string} File name
   */
  buildRulesAndCodesFileName(date: Date = new Date()): string {
    const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
    const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    return `stake_settings_${stamp}.csv`;
  }

  /**
   * Build the CSV and trigger a browser download
   * @param {IGetEventsDetailsResponse[]} events Available events
   * @param {Date} date File-name date (defaults to now)
   * @returns {boolean} False when there are no events to export
   */
  downloadRulesAndCodes(events: IGetEventsDetailsResponse[], date?: Date): boolean {
    if (!events?.length) return false;

    this.triggerDownload(
      this.buildRulesAndCodesCsv(events),
      this.buildRulesAndCodesFileName(date)
    );
    return true;
  }

  /**
   * Encode per-leg runner selections into a URL-safe token
   * @param {EncodedRunnerLeg[]} selectionRunners Per-leg runner maps
   * @returns {string} Encoded token
   */
  buildEncodedRunners(selectionRunners: EncodedRunnerLeg[]): string {
    return encodeURIComponent(btoa(JSON.stringify(selectionRunners ?? [])));
  }

  /**
   * Decode a runner token back into per-leg selections
   * @param {string} encoded Encoded token
   * @returns {EncodedRunnerLeg[]} Per-leg runner maps (empty on failure)
   */
  decodeEncodedRunners(encoded: string): EncodedRunnerLeg[] {
    if (!encoded) return [];
    try {
      return JSON.parse(atob(decodeURIComponent(encoded)));
    } catch {
      return [];
    }
  }

  /**
   * Build a shareable wager deep-link from the current selection
   * @param {IWagerShareLink} link Selection details
   * @param {string} baseHref Base URL (defaults to current location)
   * @returns {string} Deep-link URL
   */
  buildWagerShareLink(link: IWagerShareLink, baseHref: string = window.location.href): string {
    const base = baseHref.includes('#')
      ? baseHref.substring(0, baseHref.indexOf('#'))
      : baseHref;
    // OLD: replaced the decimal point with "_" (e.g. 1.00 -> 1_00).
    // const amount = `${link.baseAmount}`.replace('.', '_');
    const amount = `${link.baseAmount}`;
    const modifier = this.toFullModifier(link.modifier);
    const encoded = this.buildEncodedRunners(link.selectionRunners);

    // OLD: emitted the raw abbreviation (e.g. "BX") in the link.
    // return `${base}#/wager/${link.eventId}/${link.race}/${link.poolId}/${amount}/${link.modifier}/${encoded}`;
    return `${base}#/wager/${link.eventId}/${link.race}/${link.poolId}/${amount}/${modifier}/${encoded}`;
  }

  /**
   * Expand a modifier abbreviation to its full word (e.g. "BX" -> "Box")
   * @param {string | number} modifier Raw modifier value
   * @returns {string} Full-word modifier ("Straight" when none/unknown)
   */
  private toFullModifier(modifier: string | number): string {
    switch (`${modifier ?? ''}`) {
      case 'BX': return 'Box';
      case 'KB': return 'KeyBox';
      case 'PB': return 'PowerBox';
      case 'KW': return 'KeyWheel';
      case 'ABC': return 'ABC';
      case 'DutchCost': return 'DutchCost';
      default: return 'Straight';
    }
  }

  /**
   * Copy text to the clipboard (async API with a legacy fallback)
   * @param {string} text Text to copy
   * @returns {Promise<boolean>} True when the copy succeeded
   */
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Fall through to the legacy path below.
    }
    return this.legacyCopy(text);
  }

  /**
   * Legacy execCommand copy for browsers without the async API
   * @param {string} text Text to copy
   * @returns {boolean} True when the copy succeeded
   */
  private legacyCopy(text: string): boolean {
    const input = document.createElement('input');
    input.value = text;
    document.body.appendChild(input);
    input.select();

    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch {
      copied = false;
    }
    document.body.removeChild(input);
    return copied;
  }

  /**
   * Create a temporary anchor and trigger a file download
   * @param {string} content File contents
   * @param {string} fileName Download file name
   * @returns {void}
   */
  private triggerDownload(content: string, fileName: string): void {
    const anchor = document.createElement('a');
    anchor.href = `data:text/csv;charset=utf-8,${encodeURIComponent(content)}`;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
}
